import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { format, startOfWeek, addDays, parseISO, isSameDay } from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { plannerRequestConfig } from '../lib/plannerRequest';
import { Loader2 } from 'lucide-react';
import {
  // 8.5 (D12): shared Week <-> Day helpers. The export names below are
  // the source of truth — if a future refactor renames them in
  // `plannerGrid.js`, the same commit MUST update this import line. NO
  // inline color resolution in WeekView: the resolver is the same D2
  // precedence used by the persistent Day column header (R9 / D8).
  resolveLaneHeaderColor,
  buildCellLaneColumns,
  distributeBookingsIntoColumns,
} from './plannerGrid';

// 8.4 (D11) — defense threshold for narrow sub-cells. When a Week
// sub-cell's available width drops below this, the top variant stripe
// is reduced (2px → 1px or hidden) and inter-sub-cell padding shrinks,
// but the inter-sub-cell border-r stays at ≥1px so variant boundaries
// never collapse. Bookings of different variants NEVER share a visual
// container; the sub-cell division is mandatory.
const WEEK_SUBCELL_MIN_WIDTH_PX = 24;

// Mismo tope que `.to_list(MAX_LIST_RESULTS)` del backend (server.py). Constante
// de cliente (no se lee del backend): una respuesta de rango de EXACTAMENTE 500
// es indistinguible de una truncada y dispara el fallback por-día (D5.2).
const MAX_LIST_RESULTS = 500;

export const WeekView = ({ selectedDate, services, center, onBookingMoved }) => {
  const { api, language } = useAuth();
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const [dropError, setDropError] = useState(null);

  const weekStart = useMemo(() =>
    startOfWeek(selectedDate, { weekStartsOn: language === 'en' ? 0 : 1 }),
    [selectedDate, language]
  );
  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  // Range GET con fallback por saturación (D5.2). Colapsa las 7 GET por-día (R4)
  // en UNA request de rango y devuelve el conjunto COMPLETO de bookings de la
  // semana. Los límites Z se construyen a partir de la etiqueta de fecha LOCAL
  // (`format(day,…)`) más el sufijo literal `T00:00:00.000Z` / `T23:59:59.999Z`,
  // replicando exactamente la semántica del filtro por-día (`?date=` → `$regex
  // ^<fecha>`): el rango lexicográfico [inicio, fin] cubre el mismo conjunto que
  // las 7 requests, bordes incluidos. NO se usa `toISOString()`: convertiría a UTC
  // y desplazaría la fecha en husos != Z, perdiendo la identidad de conjunto
  // (D5.2/R4/R1). Fast path: <500 es demostrablemente completo. ===500 es
  // potencialmente truncado por `.to_list(500)`: caemos al fan-out por-día (7 GET
  // `?date=`, presupuesto de 500/día) que preserva el conjunto completo, bit a bit
  // como el camino previo a 3.2. `=== 500` exacto, no `>= 500` (`.to_list(500)`
  // nunca devuelve más de 500). Señal R7 (D8) al activar el fallback, una vez.
  // Compartido bit a bit entre el load path (useEffect) y el refetch post-move
  // (handleDrop) para no divergir (finding R4 round 2 / task 3.7).
  const fetchWeekBookings = async (signal) => {
    const extra = signal ? { signal } : {};
    const startBound = `${format(weekDays[0], 'yyyy-MM-dd')}T00:00:00.000Z`;
    const endBound = `${format(weekDays[6], 'yyyy-MM-dd')}T23:59:59.999Z`;
    const res = await api.get(
      `/bookings?start_date=${encodeURIComponent(startBound)}&end_date=${encodeURIComponent(endBound)}`,
      plannerRequestConfig(extra)
    );
    const rangeData = res.data || [];
    if (rangeData.length === MAX_LIST_RESULTS) {
      console.debug('[planner] range response saturated (500) → per-day fallback', { view: 'week' });
      const perDay = await Promise.all(
        weekDays.map(day =>
          api.get(`/bookings?date=${format(day, 'yyyy-MM-dd')}`, plannerRequestConfig(extra))
        )
      );
      return perDay.flatMap(r => r.data || []);
    }
    return rangeData;
  };

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchWeekBookings(controller.signal);
        if (!cancelled) setBookings(data);
      } catch {
        if (!cancelled) setBookings([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; controller.abort(); };
  }, [api, weekStart]); // eslint-disable-line

  const timeSlots = useMemo(() => {
    const [openHour, openMin] = (center?.openingTime || '08:00').split(':').map(Number);
    const [closeHour, closeMin] = (center?.closingTime || '21:00').split(':').map(Number);
    const slots = [];
    let h = openHour, m = openMin;
    while (h < closeHour || (h === closeHour && m < closeMin)) {
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      m += 15;
      if (m >= 60) { m -= 60; h++; }
    }
    return slots;
  }, [center]);

  const bookingsByDaySlot = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (b.status === 'CANCELLED') continue;
      const start = parseISO(b.startDateTime);
      const dayKey = format(start, 'yyyy-MM-dd');
      const h = start.getHours();
      const m = Math.floor(start.getMinutes() / 15) * 15;
      const slot = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const k = `${dayKey}|${slot}`;
      if (!map[k]) map[k] = [];
      map[k].push(b);
    }
    return map;
  }, [bookings]);

  const serviceMap = useMemo(() => {
    const m = {};
    for (const s of services) m[s.id] = s;
    return m;
  }, [services]);

  // 8.4 (D11) — measured cell widths for the 24px sub-cell defense. Each
  // Week cell that contains bookings in variant mode registers its DOM
  // node via `data-week-cell-key` and we measure its width through a
  // single shared ResizeObserver. The measured value is divided by the
  // sub-cell count at the render site to derive each sub-cell's width;
  // when ANY sub-cell falls below `WEEK_SUBCELL_MIN_WIDTH_PX`, the top
  // variant stripe is reduced (2px → 1px) but the inter-sub-cell
  // border-r stays ≥1px so the variant boundaries never collapse.
  const cellRefsRef = useRef({}); // cellKey -> HTMLDivElement | null
  const cellWidthsRef = useRef({}); // cellKey -> measured width (px)
  const [cellWidthsVersion, setCellWidthsVersion] = useState(0);

  const setWeekCellRef = useCallback((key) => (el) => {
    if (el) cellRefsRef.current[key] = el;
    else delete cellRefsRef.current[key];
  }, []);

  // Re-attach the observer whenever the set of measured cells changes.
  // We depend on `bookingsByDaySlot` so the observer is set up after
  // the new cells are mounted and torn down before unmount.
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(entries => {
      let changed = false;
      for (const entry of entries) {
        const key = entry.target && entry.target.getAttribute
          ? entry.target.getAttribute('data-week-cell-key')
          : null;
        if (!key) continue;
        const next = Math.round(entry.contentRect.width);
        if (cellWidthsRef.current[key] !== next) {
          cellWidthsRef.current[key] = next;
          changed = true;
        }
      }
      if (changed) setCellWidthsVersion(v => v + 1);
    });
    for (const el of Object.values(cellRefsRef.current)) {
      if (el) ro.observe(el);
    }
    return () => ro.disconnect();
  }, [bookingsByDaySlot, cellWidthsVersion]);

  const handleDrop = async (day, slot) => {
    if (!draggedBooking) return;
    setDragOverCell(null);

    const [h, m] = slot.split(':').map(Number);
    const newStart = new Date(day);
    newStart.setHours(h, m, 0, 0);
    const newStartISO = newStart.toISOString().replace(/\.\d{3}Z$/, '.000Z');

    if (newStartISO === draggedBooking.startDateTime) {
      setDraggedBooking(null);
      return;
    }

    // Optimistic update
    setBookings(prev => prev.map(b =>
      b.id === draggedBooking.id
        ? { ...b, startDateTime: newStartISO }
        : b
    ));

    try {
      await api.put(`/bookings/${draggedBooking.id}`, {
        calendarId: draggedBooking.calendarId,
        startDateTime: newStartISO,
      });
      // Refetch tras mover vía el MISMO helper que la carga: 1 range GET con
      // fallback por-día ante saturación a 500 (D5.2). Sin esto, un rango con
      // >500 bookings perdía filas silenciosamente tras un move (finding R4
      // round 2 / task 3.7).
      setBookings(await fetchWeekBookings());
      if (onBookingMoved) onBookingMoved();
    } catch (err) {
      // Revertir optimistic update
      setBookings(prev => prev.map(b =>
        b.id === draggedBooking.id
          ? { ...b, startDateTime: draggedBooking.startDateTime }
          : b
      ));
      const detail = err?.response?.data?.detail || 'No se pudo mover la cita';
      setDropError(detail);
      setTimeout(() => setDropError(null), 4000);
    } finally {
      setDraggedBooking(null);
    }
  };

  const [openHour, openMin] = (center?.openingTime || '08:00').split(':').map(Number);
  const [closeHour, closeMin] = (center?.closingTime || '21:00').split(':').map(Number);

  const isValidDropSlot = (slot, slotsPerBooking) => {
    const [h, m] = slot.split(':').map(Number);
    const slotMinutes = h * 60 + m;
    const openMinutes = openHour * 60 + openMin;
    const closeMinutes = closeHour * 60 + closeMin;
    const durationMinutes = (slotsPerBooking || 2) * 15;
    return slotMinutes >= openMinutes && (slotMinutes + durationMinutes) <= closeMinutes;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto planner-scroll">
      <div className="min-w-[700px]">
        {/* Day headers */}
        <div className="flex border-b border-zinc-800 sticky top-0 z-40 bg-background/95 backdrop-blur-md">
          <div className="w-16 flex-shrink-0 border-r border-zinc-800" />
          {weekDays.map((day) => {
            const isToday = isSameDay(day, new Date());
            return (
              <div key={day.toISOString()} className="flex-1 min-w-[100px] px-2 py-3 border-r border-zinc-800 last:border-r-0 text-center">
                <div className={`text-[10px] uppercase tracking-wide ${isToday ? 'text-indigo-400' : 'text-zinc-500'}`}>
                  {format(day, 'EEE', { locale })}
                </div>
                <div className={`text-sm font-semibold mt-0.5 ${isToday ? 'text-indigo-300' : 'text-zinc-200'}`}>
                  {format(day, 'd')}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time rows */}
        {timeSlots.map((slot) => (
          <div key={slot} className="flex border-b border-zinc-800/40 min-h-[44px]">
            <div className="w-16 flex-shrink-0 px-2 pt-1 border-r border-zinc-800 text-right">
              <span className="text-[10px] text-zinc-600">{slot}</span>
            </div>
            {weekDays.map((day) => {
              const k = `${format(day, 'yyyy-MM-dd')}|${slot}`;
              const cellBookings = bookingsByDaySlot[k] || [];
              const svcOfDragged = draggedBooking ? serviceMap[draggedBooking.calendarId] : null;
              const slotValid = draggedBooking
                ? isValidDropSlot(slot, svcOfDragged?.slotsPerBooking || 2)
                : true;
              const isOver = dragOverCell === k;

              // 8.3 (D11) — decide sub-cell rendering for this cell.
              // The master service is the one whose `enablePlanningVariants`
              // and active variants we evaluate. When the cell has no
              // bookings, OR feature OFF, OR < 2 active variants, we
              // keep the legacy single-cell render (no sub-cells, no
              // mixing). Otherwise we materialize sub-cells per
              // `buildCellLaneColumns(service, cellBookings)`.
              const masterService = cellBookings.length > 0
                ? serviceMap[cellBookings[0].calendarId]
                : null;
              const activeCount = (masterService?.variants || [])
                .filter(v => v.active !== false).length;
              const variantsActive = masterService?.enablePlanningVariants === true
                && activeCount >= 2;
              const cellColumns = variantsActive
                ? buildCellLaneColumns(masterService, cellBookings)
                : [];
              const useSubcells = cellColumns.length > 0;
              const measuredCellWidth = cellWidthsRef.current[k] || 0;
              // Sub-cell width: divide measured cell width by the lane
              // count. When measurement is not yet available (initial
              // render), assume a wide layout so the full 2px stripe
              // shows by default — the observer narrows it later.
              const subcellWidth = useSubcells && measuredCellWidth > 0
                ? Math.floor(measuredCellWidth / cellColumns.length)
                : Number.POSITIVE_INFINITY;
              const narrowSubcells = useSubcells && subcellWidth < WEEK_SUBCELL_MIN_WIDTH_PX;

              return (
                <div
                  key={day.toISOString()}
                  data-week-cell-key={k}
                  ref={useSubcells ? setWeekCellRef(k) : undefined}
                  className={`flex-1 min-w-[100px] border-r border-zinc-800 last:border-r-0
                    ${useSubcells ? 'p-0' : 'px-1 py-0.5 space-y-0.5'}
                    ${draggedBooking && slotValid ? 'hover:bg-zinc-800/40' : ''}
                    ${isOver && slotValid ? 'bg-zinc-700/40 ring-1 ring-inset ring-zinc-500' : ''}
                    ${draggedBooking && !slotValid ? 'opacity-30' : ''}`}
                  onDragOver={(e) => {
                    if (!draggedBooking || !slotValid) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDragOverCell(k);
                  }}
                  onDragLeave={() => setDragOverCell(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!slotValid) return;
                    handleDrop(day, slot);
                  }}
                >
                  {useSubcells ? (
                    // 8.3 (D11): one sub-cell per column of
                    // `buildCellLaneColumns`, in the helper's order.
                    // Each sub-cell carries a top stripe of
                    // `variant.color || service.color` via the shared
                    // `resolveLaneHeaderColor` resolver (D12), a
                    // minimum 1px border-r between siblings, and the
                    // bookings distributed via
                    // `distributeBookingsIntoColumns`.
                    (() => {
                      const distributed = distributeBookingsIntoColumns(
                        masterService, cellBookings, cellColumns,
                      );
                      return (
                        <div
                          className="flex w-full h-full"
                          data-testid={`week-subcells-${k}`}
                        >
                          {distributed.map((lane, laneIdx) => {
                            const stripeColor = resolveLaneHeaderColor(
                              masterService, { variantId: lane.variantId },
                            );
                            const stripeHeight = narrowSubcells ? '1px' : '2px';
                            return (
                              <div
                                key={lane.key}
                                data-testid={`week-subcell-${k}-${lane.key}`}
                                data-lane-kind={lane.isLegacy ? 'legacy' : lane.isUnknown ? 'unknown' : lane.isInactive ? 'inactive' : 'active'}
                                className="relative flex-1 min-w-0 border-r border-zinc-800/40 last:border-r-0"
                                style={{
                                  // 8.4 (D11): stripe (top) reduced to
                                  // 1px under 24px sub-cell width; full
                                  // 2px otherwise. The inter-sub-cell
                                  // border-r is the variant boundary
                                  // and stays ≥1px regardless of width.
                                  borderTop: `${stripeHeight} solid ${stripeColor || '#52525b'}`,
                                  // 8.4: drop the inter-sub-cell
                                  // padding under 24px so the bookings
                                  // inside have a chance to render.
                                  padding: narrowSubcells ? '1px' : '2px',
                                }}
                              >
                                {lane.bookings.map(b => {
                                  const svc = serviceMap[b.calendarId];
                                  return (
                                    <div
                                      key={b.id || b.bookingId}
                                      draggable={true}
                                      onDragStart={(e) => {
                                        setDraggedBooking(b);
                                        e.dataTransfer.effectAllowed = 'move';
                                      }}
                                      onDragEnd={() => setDraggedBooking(null)}
                                      className={`rounded text-[10px] truncate cursor-grab active:cursor-grabbing mb-0.5
                                        ${draggedBooking?.id === b.id ? 'opacity-40' : ''}`}
                                      style={{
                                        // 8.1 (D10, R9.4): restored
                                        // EXACTLY to the pre-3.1
                                        // expression bit-for-bit
                                        // (verified against
                                        // origin/main:255-262). Source
                                        // of truth is the SERVICE
                                        // master color, NOT the
                                        // variant color. The 2px solid
                                        // border-left and the alpha-22
                                        // background are derived from
                                        // the same `svc.color` so the
                                        // card is visually consistent
                                        // across the Week view.
                                        backgroundColor: svc?.color ? `${svc.color}22` : '#27272a',
                                        borderLeft: `2px solid ${svc?.color || '#52525b'}`,
                                        padding: '1px 4px',
                                      }}
                                      title={`${b.clientName || ''} — ${svc?.name || ''}`}
                                      data-booking-id={b.id || b.bookingId}
                                    >
                                      {(b.clientName || '—').split(' ')[0]}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  ) : (
                    // Single-cell legacy render — preserved exactly as
                    // before D11 for cells without the variant mode
                    // (feature OFF / <2 variants / no bookings).
                    cellBookings.map(b => {
                      const svc = serviceMap[b.calendarId];
                      return (
                        <div
                          key={b.id || b.bookingId}
                          draggable={true}
                          onDragStart={(e) => {
                            setDraggedBooking(b);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => setDraggedBooking(null)}
                          className={`rounded px-1.5 py-0.5 text-[10px] truncate cursor-grab active:cursor-grabbing
                            ${draggedBooking?.id === b.id ? 'opacity-40' : ''}`}
                          style={{
                            // 8.1 (D10, R9.4): same pre-3.1 expression
                            // bit-for-bit as the sub-cell render.
                            backgroundColor: svc?.color ? `${svc.color}22` : '#27272a',
                            borderLeft: `2px solid ${svc?.color || '#52525b'}`,
                          }}
                          title={`${b.clientName || ''} — ${svc?.name || ''}`}
                          data-booking-id={b.id || b.bookingId}
                        >
                          {(b.clientName || '—').split(' ')[0]}
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {dropError && (
        <div className="fixed bottom-4 right-4 bg-red-900/90 text-red-200 text-sm px-4 py-3 rounded-lg shadow-lg z-50 max-w-sm">
          ⚠️ {dropError}
        </div>
      )}
    </div>
  );
};
